import { getConfigValue } from "../config";

export type RequestOptions = {
  headers?: HttpHeaders;
  params?: HttpQueryParameters;
  data?: any;
  timeout?: number;
  signal?: AbortSignal;
};

export type HttpResponse<T = any> = {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
};

export type HttpError = {
  message: string;
  status?: number;
  statusText?: string;
  data?: any;
};

export type HttpHeaders = Record<string, string>;
export type HttpQueryParameters = Record<string, string | number | boolean>;
export type HttpRequestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type HttpClientOptions = {
  baseUrl?: string;
  affiliateId?: string;
  headers?: Record<string, string>;
  timeout?: number;
};

/**
 * Unified HTTP client that can be used across all frontend applications.
 * It provides a simple, consistent API for making API requests, regardless
 * of whether the underlying implementation uses fetch or axios.
 */
export class HttpClient {
  private baseUrl: string;
  private headers: Record<string, string>;
  private timeout: number;

  constructor(options: HttpClientOptions = {}) {
    const { baseUrl, headers, timeout, affiliateId } = options;
    this.baseUrl =
      baseUrl || `${getConfigValue("apiUrl")}/affiliates/${affiliateId}`;
    this.headers = {
      "Content-Type": "application/ld+json",
      Accept: "application/ld+json",
      ...headers,
    };
    this.timeout = timeout || 30000;
  }

  /**
   * Set the bearer token for authorization
   */
  setBearerToken(token: string): this {
    this.headers["Authorization"] = `Bearer ${token}`;
    return this;
  }

  /**
   * Make a GET request
   */
  async get<T = any>(
    url: string,
    options: RequestOptions = {},
  ): Promise<HttpResponse<T>> {
    return this.request<T>("GET", url, options);
  }

  /**
   * Make a POST request
   */
  async post<T = any>(
    url: string,
    options: RequestOptions = {},
  ): Promise<HttpResponse<T>> {
    return this.request<T>("POST", url, options);
  }

  /**
   * Make a PUT request
   */
  async put<T = any>(
    url: string,
    options: RequestOptions = {},
  ): Promise<HttpResponse<T>> {
    return this.request<T>("PUT", url, options);
  }

  /**
   * Make a PATCH request
   */
  async patch<T = any>(
    url: string,
    options: RequestOptions = {},
  ): Promise<HttpResponse<T>> {
    return this.request<T>("PATCH", url, options);
  }

  /**
   * Make a DELETE request
   */
  async delete<T = any>(
    url: string,
    options: RequestOptions = {},
  ): Promise<HttpResponse<T>> {
    return this.request<T>("DELETE", url, options);
  }

  /**
   * Generic request method that handles all request types
   */
  async request<T = any>(
    method: HttpRequestMethod,
    url: string,
    {
      headers = {},
      params = {},
      data,
      signal,
      timeout = this.timeout,
    }: RequestOptions,
  ): Promise<HttpResponse<T>> {
    const fullUrl = this.buildUrl(url, params);

    const controller = new AbortController();
    const requestSignal = signal || controller.signal;

    // Set up timeout if needed
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // When sending FormData, don't set Content-Type so browser can add the proper boundary
      let requestHeaders: Record<string, string> = {
        ...this.headers,
        ...headers,
      };

      if (data instanceof FormData) {
        // Remove Content-Type header for FormData requests to let browser set it with boundary
        const {
          "Content-Type": _,
          Accept: accept,
          ...restHeaders
        } = requestHeaders;
        requestHeaders = { Accept: accept, ...restHeaders };
      }

      const response = await fetch(fullUrl, {
        method,
        headers: requestHeaders,
        body:
          data instanceof URLSearchParams || data instanceof FormData
            ? data
            : data
              ? JSON.stringify(data)
              : undefined,
        signal: requestSignal,
      });

      clearTimeout(timeoutId);

      const responseData = await this.parseResponse<T>(response);
      if (!response.ok) {
        throw {
          message: `Request failed with status ${response.status}`,
          status: response.status,
          statusText: response.statusText,
          data: responseData,
        };
      }

      return {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
        headers: this.extractHeaders(response),
      };
    } catch (error: unknown) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === "AbortError") {
        throw {
          message: "Request timeout",
          status: 0,
        };
      }

      throw error;
    }
  }

  /**
   * Parse response based on content type
   */
  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");

    if (
      contentType?.includes("application/ld+json") ||
      contentType?.includes("application/json") ||
      contentType?.includes("application/problem+json")
    ) {
      return response.json();
    } else if (contentType?.includes("text/")) {
      return (await response.text()) as unknown as T;
    } else {
      return (await response.blob()) as unknown as T;
    }
  }

  /**
   * Extract headers from fetch Response object
   */
  private extractHeaders(response: Response): Record<string, string> {
    const headers: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      headers[key] = value;
    });
    return headers;
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(
    url: string,
    params: Record<string, string | number | boolean> = {},
  ): string {
    const fullUrl = url.startsWith("http") ? url : `${this.baseUrl}${url}`;
    const searchParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        searchParams.append(key, String(params[key]));
      }
    });

    const queryString = searchParams.toString();
    if (queryString) {
      return `${fullUrl}${fullUrl.includes("?") ? "&" : "?"}${queryString}`;
    }

    return fullUrl;
  }
}

// Export a default instance for convenience
export const httpClient = new HttpClient();

// Also export a factory function to create custom instances
export const createHttpClient = (options?: HttpClientOptions) => {
  return new HttpClient(options);
};
